import { ICriteria } from '../../contracts/criteria.contract';

/** Vessel-type criteria keys that participate in the hierarchy. */
export type VesselTypeKey =
    | 'PassengerShip'
    | 'CruisePassenger'
    | 'RoRoVessel'
    | 'RoRoVehicleCarrier'
    | 'RoRoCargoShip'
    | 'RoRoPassengerShip'
    | 'HighSpeedCraft'
    | 'CargoShip'
    | 'ContainerCarrier'
    | 'GeneralCargoCarrier'
    | 'RefrigeratedCargoCarrier'
    | 'Tanker'
    | 'OilTanker'
    | 'CrudeTanker'
    | 'ProductTanker'
    | 'ChemicalCarrier'
    | 'GasLNGCarrier'
    | 'LNGCarrier'
    | 'NonLNGGasCarrier'
    | 'BulkCarrier'
    | 'CombinationCarrier'
    | 'OreCarrier'
    | 'OffshoreSupportVessel'
    | 'FishingVessel'
    | 'DynamicallySupportedCraft'
    | 'MobileOffshoreDrillingUnit'
    | 'SelfPropelled';

/** Direct children only; descendants are resolved recursively. */
const VESSEL_TYPE_CHILDREN: Partial<Record<VesselTypeKey, VesselTypeKey[]>> = {
    PassengerShip: ['CruisePassenger'],
    RoRoVessel: ['RoRoVehicleCarrier', 'RoRoCargoShip', 'RoRoPassengerShip'],
    CargoShip: [
        'ContainerCarrier',
        'GeneralCargoCarrier',
        'RefrigeratedCargoCarrier',
        'Tanker',
        'BulkCarrier',
        'CombinationCarrier',
        'OreCarrier',
        'OffshoreSupportVessel'
    ],
    Tanker: ['OilTanker', 'ChemicalCarrier', 'GasLNGCarrier'],
    OilTanker: ['CrudeTanker', 'ProductTanker'],
    GasLNGCarrier: ['LNGCarrier', 'NonLNGGasCarrier']
};

const getDescendants = (key: VesselTypeKey): VesselTypeKey[] => {
    const children = VESSEL_TYPE_CHILDREN[key] ?? [];
    return children.flatMap((child) => [child, ...getDescendants(child)]);
};

const VESSEL_TYPE_KEY_SET = new Set<string>([
    'PassengerShip',
    'CruisePassenger',
    'RoRoVessel',
    'RoRoVehicleCarrier',
    'RoRoCargoShip',
    'RoRoPassengerShip',
    'HighSpeedCraft',
    'CargoShip',
    'ContainerCarrier',
    'GeneralCargoCarrier',
    'RefrigeratedCargoCarrier',
    'Tanker',
    'OilTanker',
    'CrudeTanker',
    'ProductTanker',
    'ChemicalCarrier',
    'GasLNGCarrier',
    'LNGCarrier',
    'NonLNGGasCarrier',
    'BulkCarrier',
    'CombinationCarrier',
    'OreCarrier',
    'OffshoreSupportVessel',
    'FishingVessel',
    'DynamicallySupportedCraft',
    'MobileOffshoreDrillingUnit',
    'SelfPropelled'
]);

export const isVesselTypeKey = (key: string): key is VesselTypeKey => VESSEL_TYPE_KEY_SET.has(key);

/**
 * Toggle a vessel type and cascade the same value to all succeeding (descendant) levels.
 * Selecting a parent auto-selects descendants; deselecting a parent clears descendants.
 * Ancestors are left unchanged (backend preceding logic still applies).
 */
export const applyVesselTypeCascade = (criteria: ICriteria, key: VesselTypeKey): ICriteria => {
    const nextValue = !criteria[key];
    const updates: Partial<ICriteria> = { [key]: nextValue };

    for (const descendant of getDescendants(key)) {
        updates[descendant] = nextValue;
    }

    return { ...criteria, ...updates };
};

/** Force a vessel type (and its descendants) off — used when clearing a Filters Selected chip. */
export const clearVesselTypeCascade = (criteria: ICriteria, key: VesselTypeKey): ICriteria => {
    const updates: Partial<ICriteria> = { [key]: false };

    for (const descendant of getDescendants(key)) {
        updates[descendant] = false;
    }

    return { ...criteria, ...updates };
};
